import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { Store, StoreShopifyInfo, StoreShopifySyncInfo } from "../../../types";
import Divider from '@mui/material/Divider';
import { createLog } from "../../../lib/admin";
import { editShopifyHydrogenInfo, editShopifySyncInfo, editStoreShopify } from "../../../lib/shopifyInfo";
import { maskSecret } from "../../../utils/maskSecret";
import StoreHydrogenForm, { HydrogenValues } from "./StoreHydrogenForm";
import StoreShopifySyncForm, { SyncAppValues } from "./StoreShopifySyncForm";
import StickySaveBar from "../../global/StickySaveBar";
import ConfirmModal from "../../global/ConfirmModal";
import Notification from "../../global/Notification";
import SalesChannelsSection from "./salesChannels/SalesChannelsSection";
import { useHasScope } from "../../../utils/Permission";
import constants from "../../../lib/constants";
import shopifyIcon from "../../../assets/management/shopify.svg";

type Props =
   {
      store: Store,
      hasUnsavedChanges: boolean,
      setHasUnsavedChanges: (flag: boolean) => void,
      hasBeenUpdated: boolean,
      setHasBeenUpdated: (flag: boolean) => void
   }

type ShopifyValues = {
   storeUrl: string,
   orderWebhookId: string,
   salesChannels: string,
}

type FormSections = {
   shopify: ShopifyValues,
   syncApp: SyncAppValues,
   hydrogen: HydrogenValues,
}

const normalize = (value: string | null | undefined) =>
   value == null || value === "null" ? "" : value;

const sectionsFromStore = (store: Store): FormSections => ({
   shopify: {
      storeUrl: normalize(store.storeUrl),
      orderWebhookId: normalize(store.orderWebhookId),
      salesChannels: normalize(store.shopifyHydrogenPublicationId),
   },
   syncApp: {
      apiSecretKey: normalize(store.syncApiSecret),
      apiKey: normalize(store.syncApiKey),
      adminToken: normalize(store.syncAdminToken),
   },
   hydrogen: {
      apiSecretKey: normalize(store.hydrogenApiSecret),
      apiKey: normalize(store.hydrogenApiKey),
      adminToken: normalize(store.hydrogenAdminToken),
      storefrontToken: normalize(store.hydrogenStorefrontToken),
   },
});

const isSectionDirty = (draft: Record<string, string>, base: Record<string, string>) =>
   Object.keys(base).some((key) => draft[key] !== base[key]);

const listLabels = (labels: string[]) =>
   labels.length > 1
      ? `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`
      : labels[0];

function mergeSection<T extends Record<string, string>>(draft: T, prevBase: T, nextBase: T): T {
   const merged = { ...nextBase };
   for (const key of Object.keys(nextBase) as (keyof T)[]) {
      if (draft[key] !== prevBase[key]) {
         merged[key] = draft[key];
      }
   }
   return merged;
}

export default function StoreShopifyForm({ store, setHasUnsavedChanges, setHasBeenUpdated }: Props) {

   const [baseline, setBaseline] = useState<FormSections>(() => sectionsFromStore(store));
   const [drafts, setDrafts] = useState<FormSections>(baseline);
   const [confirmOpen, setConfirmOpen] = useState(false);
   const [saving, setSaving] = useState(false);
   const [saveError, setSaveError] = useState<string | null>(null);
   const [successMessage, setSuccessMessage] = useState<string | null>(null);
   const canUpdateStoreUrl = useHasScope(constants.SHOPIFY_INFO_SCOPES.STORE_URL);
   const canUpdateAppCredentials = useHasScope(constants.SHOPIFY_INFO_SCOPES.APP_CREDENTIALS);
   const canUpdateSalesChannels = useHasScope(constants.SHOPIFY_INFO_SCOPES.SALES_CHANNELS);

   const dismissSaveError = useCallback(() => setSaveError(null), []);
   const closeSuccessMessage = useCallback(() => setSuccessMessage(null), []);

   const baselineRef = useRef(baseline);
   baselineRef.current = baseline;

   useEffect(() => {
      const next = sectionsFromStore(store);
      const prev = baselineRef.current;
      setDrafts((current) => ({
         shopify: mergeSection(current.shopify, prev.shopify, next.shopify),
         syncApp: mergeSection(current.syncApp, prev.syncApp, next.syncApp),
         hydrogen: mergeSection(current.hydrogen, prev.hydrogen, next.hydrogen),
      }));
      setBaseline(next);
   }, [store]);

   const dirty = {
      shopify: isSectionDirty(drafts.shopify, baseline.shopify),
      syncApp: isSectionDirty(drafts.syncApp, baseline.syncApp),
      hydrogen: isSectionDirty(drafts.hydrogen, baseline.hydrogen),
   };
   const hasChanges = dirty.shopify || dirty.syncApp || dirty.hydrogen;

   const dirtyStoreUrl = drafts.shopify.storeUrl !== baseline.shopify.storeUrl
      || drafts.shopify.orderWebhookId !== baseline.shopify.orderWebhookId;
   const dirtySalesChannels = drafts.shopify.salesChannels !== baseline.shopify.salesChannels;

   useEffect(() => {
      setHasUnsavedChanges(hasChanges);
   }, [hasChanges, setHasUnsavedChanges]);

   const handleShopifyChange = (event: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      setDrafts((current) => ({ ...current, shopify: { ...current.shopify, [name]: value } }));
   };

   const handleSyncAppChange = (field: keyof SyncAppValues, value: string) => {
      setDrafts((current) => ({ ...current, syncApp: { ...current.syncApp, [field]: value } }));
   };

   const handleHydrogenChange = (field: keyof HydrogenValues, value: string) => {
      setDrafts((current) => ({ ...current, hydrogen: { ...current.hydrogen, [field]: value } }));
   };

   const handleSalesChannelsChange = (salesChannels: string) => {
      setDrafts((current) => ({ ...current, shopify: { ...current.shopify, salesChannels } }));
   };

   const missingScopeFor = () => {
      if (dirtyStoreUrl && !canUpdateStoreUrl) return "the Shopify store URL";
      if (dirtySalesChannels && !canUpdateSalesChannels) return "the sales channels";
      if ((dirty.syncApp || dirty.hydrogen) && !canUpdateAppCredentials) return "the app credentials";
      return null;
   };

   const handleSaveClick = () => {
      const missingScope = missingScopeFor();
      if (missingScope) {
         setSaveError(`You do not have permission to update ${missingScope} for this store.`);
         return;
      }
      if (dirtyStoreUrl) {
         const url = drafts.shopify.storeUrl.trim();
         if (url === "" || !url.endsWith("myshopify.com")) {
            setSaveError("Select a valid Shopify store Url");
            return;
         }
      }
      setSaveError(null);
      setConfirmOpen(true);
   };

   const maskValues = <T extends Record<string, string>>(values: T) =>
      Object.fromEntries(Object.entries(values).map(([field, value]) => [field, maskSecret(value)]));

   const logSave = (section: string, lastValue: unknown, newValue: unknown) => {
      createLog({ storeId: store.id, entityType: 'storeSettings', section, action: 'save', lastValue, newValue });
   };

   const handleConfirmSave = async () => {
      setConfirmOpen(false);
      setSaving(true);

      const operations: { label: string, run: () => Promise<unknown>, apply: () => void, log: () => void }[] = [];

      if (dirty.shopify) {
         // Only the parts that changed are sent, so the backend never receives an attribute the
         // user has no scope for and never rewrites a column they did not touch
         const payload: StoreShopifyInfo = {
            id: store.id,
            ...(dirtyStoreUrl && {
               storeurl: drafts.shopify.storeUrl.trim(),
               orderwebhookid: drafts.shopify.orderWebhookId.trim(),
            }),
            ...(dirtySalesChannels && { shopifyhydrogenpublicationid: drafts.shopify.salesChannels }),
         };
         const saved: ShopifyValues = {
            storeUrl: payload.storeurl ?? drafts.shopify.storeUrl,
            orderWebhookId: payload.orderwebhookid ?? drafts.shopify.orderWebhookId,
            salesChannels: drafts.shopify.salesChannels,
         };
         operations.push({
            label: "Shopify",
            run: () => editStoreShopify(payload),
            apply: () => {
               setDrafts((current) => ({ ...current, shopify: saved }));
               setBaseline((current) => ({ ...current, shopify: saved }));
            },
            log: () => logSave('shopify', baseline.shopify, saved),
         });
      }

      if (dirty.syncApp) {
         const payload: StoreShopifySyncInfo = {
            id: store.id,
            apiSecretKey: drafts.syncApp.apiSecretKey.trim(),
            apiKey: drafts.syncApp.apiKey.trim(),
            apiToken: drafts.syncApp.adminToken.trim(),
         };
         const saved: SyncAppValues = {
            apiSecretKey: payload.apiSecretKey,
            apiKey: payload.apiKey,
            adminToken: payload.apiToken,
         };
         operations.push({
            label: "Shopify Sync App",
            run: () => editShopifySyncInfo(payload),
            apply: () => {
               setDrafts((current) => ({ ...current, syncApp: saved }));
               setBaseline((current) => ({ ...current, syncApp: saved }));
            },
            log: () => logSave('shopify', maskValues(baseline.syncApp), maskValues(saved)),
         });
      }

      if (dirty.hydrogen) {
         const payload: StoreShopifySyncInfo = {
            id: store.id,
            apiSecretKey: drafts.hydrogen.apiSecretKey.trim(),
            apiKey: drafts.hydrogen.apiKey.trim(),
            apiToken: drafts.hydrogen.storefrontToken.trim(),
            apiAdminToken: drafts.hydrogen.adminToken.trim(),
         };
         const saved: HydrogenValues = {
            apiSecretKey: payload.apiSecretKey,
            apiKey: payload.apiKey,
            adminToken: payload.apiAdminToken ?? "",
            storefrontToken: payload.apiToken,
         };
         operations.push({
            label: "Shopify Hydrogen",
            run: () => editShopifyHydrogenInfo(payload),
            apply: () => {
               setDrafts((current) => ({ ...current, hydrogen: saved }));
               setBaseline((current) => ({ ...current, hydrogen: saved }));
            },
            log: () => logSave('shopify', maskValues(baseline.hydrogen), maskValues(saved)),
         });
      }

      const results = await Promise.allSettled(operations.map((operation) => operation.run()));

      const savedLabels: string[] = [];
      const failedLabels: string[] = [];
      results.forEach((result, index) => {
         if (result.status === "fulfilled") {
            operations[index].apply();
            operations[index].log();
            savedLabels.push(operations[index].label);
         } else {
            failedLabels.push(operations[index].label);
            console.error(`Error saving ${operations[index].label}:`, result.reason);
         }
      });

      if (savedLabels.length > 0) {
         setHasBeenUpdated(true);
      }
      if (failedLabels.length === 0) {
         setSuccessMessage("Changes saved successfully.");
      } else if (savedLabels.length === 0) {
         setSaveError("Could not save changes. Please try again later.");
      } else {
         setSaveError(`Could not save changes in the ${listLabels(failedLabels)} ${failedLabels.length > 1 ? "sections" : "section"}. Please try again later.`);
      }
      setSaving(false);
   };

   const handleCancelChanges = () => {
      setDrafts(baseline);
      setSaveError(null);
   };

   return (
      <>
         {canUpdateStoreUrl && (
            <div className="sync-management">

               <div className="sync-header">
                  <p>Shopify Stores/{store.friendlyName}</p>
                  <h1> <img src={shopifyIcon} alt="" />Shopify</h1>
               </div>
               <Divider className="divider" />
               <div className="form">

                  <div className="field">
                     <label htmlFor="shopifyUrl" className="field-label">Shopify Store URL</label>
                     <input type="text" id="shopifyUrl" placeholder="" className="field-input" name="storeUrl" value={drafts.shopify.storeUrl} onChange={handleShopifyChange} />
                  </div>
                  <div className="field">
                     <label htmlFor="orderWebHookId" className="field-label">Order WebHookId</label>
                     <input type="text" id="orderWebHookId" className="field-input" name="orderWebhookId" value={drafts.shopify.orderWebhookId} onChange={handleShopifyChange} />
                  </div>

               </div>
            </div>
         )}
         {canUpdateAppCredentials && (
            <div className="sync-management-forms">
               <StoreShopifySyncForm friendlyName={store.friendlyName} values={drafts.syncApp} onChange={handleSyncAppChange} />
               <StoreHydrogenForm friendlyName={store.friendlyName} values={drafts.hydrogen} onChange={handleHydrogenChange} />
            </div>
         )}
         <SalesChannelsSection
            storeId={store.id}
            friendlyName={store.friendlyName}
            value={drafts.shopify.salesChannels}
            canEdit={canUpdateSalesChannels}
            onChange={handleSalesChannelsChange}
         />
         <StickySaveBar
            visible={hasChanges}
            saving={saving}
            onSave={handleSaveClick}
            onCancel={handleCancelChanges}
            alignWithSelector=".leftVertical"
            errorMessage={saveError ?? undefined}
            onErrorDismiss={dismissSaveError}
         />
         <Notification message={successMessage} type="success" onClose={closeSuccessMessage} />
         <ConfirmModal
            open={confirmOpen}
            title="Save changes"
            message="Are you sure? If data is not correct, synchronization will not work for this store."
            onCancel={() => setConfirmOpen(false)}
            onConfirm={handleConfirmSave}
            confirmLabel="Save"
            confirmColor="primary"
         />
      </>
   );
}